(() => {
  const rootPath = window.Shopify?.routes?.root || '/';

  const formatPrice = (cents) => `${(cents / 100).toLocaleString('hu-HU').replace(/\s/g, '.')} Ft`;
  const selectedPrice = (select) => Number(select?.selectedOptions[0]?.dataset.price || 0);
  const selectionTotal = (selects, twoDiscount = 0, threeDiscount = 0) => {
    const prices = selects.map(selectedPrice);
    const bundleDiscount = prices.length === 2 ? twoDiscount : prices.length === 3 ? threeDiscount : 0;
    const saving = Math.min(bundleDiscount, ...prices);
    return prices.reduce((sum, price) => sum + price, 0) - saving;
  };
  const selectionAvailable = (select) => {
    const option = select.selectedOptions[0];
    return Boolean(option?.value) && !option.disabled;
  };

  const addItemsToCart = async (items) => {
    // Repeated colours are the same product, not separate discounted bundle lines.
    const quantities = new Map();
    items.forEach(({ id, quantity }) => quantities.set(id, (quantities.get(id) || 0) + quantity));
    const cartItems = [...quantities].map(([id, quantity]) => ({ id, quantity }));
    const response = await fetch(`${rootPath}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: cartItems }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'A termékeket nem sikerült a kosárba tenni.');
    }

    return response.json();
  };

  const goToCart = () => {
    window.location.assign(`${rootPath}cart`);
  };

  const setButtonState = (button, busy, text) => {
    if (!button) return;
    if (!button.dataset.pgOriginalText) button.dataset.pgOriginalText = button.textContent.trim();
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.textContent = text || button.dataset.pgOriginalText;
  };

  const initHomeBundle = (bundle) => {
    if (bundle.dataset.pgBound === 'true') return;
    bundle.dataset.pgBound = 'true';

    const form = bundle.querySelector('[data-pg-bundle-form]');
    if (!form) return;

    const modeInputs = [...form.querySelectorAll('[data-pg-bundle-mode]')];
    const selects = [...form.querySelectorAll('[data-pg-bundle-select]')];
    const thirdPosition = form.querySelector('[data-pg-bundle-position="3"]');
    const thirdSelect = thirdPosition?.querySelector('[data-pg-bundle-select="3"]');
    const thirdQuantity = thirdPosition?.querySelector('[data-pg-bundle-quantity="3"]');
    const submit = form.querySelector('[data-pg-bundle-submit]');
    const status = form.querySelector('[data-pg-bundle-status]');
    const total = form.querySelector('[data-pg-bundle-total]');
    const twoDiscount = Number(form.dataset.pgTwoDiscount || 0);
    const threeDiscount = Number(form.dataset.pgThreeDiscount || 0);

    const syncMode = () => {
      const mode = Number(modeInputs.find((input) => input.checked)?.value || 2);
      const showThird = mode === 3;
      if (thirdPosition) thirdPosition.hidden = !showThird;
      if (thirdSelect) thirdSelect.disabled = !showThird;
      if (thirdQuantity) thirdQuantity.disabled = !showThird;
      const activeSelects = selects.slice(0, mode);
      if (total) total.textContent = formatPrice(selectionTotal(activeSelects, twoDiscount, threeDiscount));
      [2, 3].forEach((count) => {
        const price = form.querySelector(`[data-pg-mode-total="${count}"]`);
        if (price) price.textContent = formatPrice(selectionTotal(selects.slice(0, count), twoDiscount, threeDiscount));
      });
      if (submit) submit.disabled = !activeSelects.every(selectionAvailable);
    };

    modeInputs.forEach((input) => input.addEventListener('change', syncMode));
    selects.forEach((select) => select.addEventListener('change', syncMode));
    syncMode();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const mode = Number(modeInputs.find((input) => input.checked)?.value || 2);
      const selected = [...form.querySelectorAll('[data-pg-bundle-select]')]
        .filter((select) => !select.disabled)
        .slice(0, mode);
      const items = selected.map((select) => ({
        id: Number(select.value),
        quantity: 1,
      }));

      if (items.length !== mode || items.some((item) => !item.id) || !selected.every(selectionAvailable)) {
        if (status) status.textContent = 'Kérlek, válassz színt minden táskához.';
        return;
      }

      try {
        if (status) status.textContent = '';
        setButtonState(submit, true, 'Hozzáadás…');
        await addItemsToCart(items);
        setButtonState(submit, true, 'A kosárban ✓');
        goToCart();
      } catch (error) {
        if (status) status.textContent = error.message;
        setButtonState(submit, false);
      }
    });
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.pgBound === 'true') return;
    gallery.dataset.pgBound = 'true';

    const thumbs = [...gallery.querySelectorAll('[data-pg-gallery-thumb]')];
    const panels = [...gallery.querySelectorAll('[data-pg-gallery-panel]')];
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const target = String(thumb.dataset.pgGalleryThumb);
        thumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('pg-product__gallery-thumbnail--active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        panels.forEach((panel) => {
          const active = String(panel.dataset.pgGalleryPanel) === target;
          panel.hidden = !active;
          panel.classList.toggle('pg-product__gallery-panel--active', active);
        });
      });
    });
  };

  const initProductForm = (wrapper) => {
    if (wrapper.dataset.pgBound === 'true') return;
    wrapper.dataset.pgBound = 'true';

    const form = wrapper.querySelector('form');
    if (!form) return;

    const modes = [...form.querySelectorAll('[data-pg-bundle-mode]')];
    const panels = [...form.querySelectorAll('[data-pg-bundle-panel]')];
    const offers = [...form.querySelectorAll('.pg-product__offer')];
    const button = form.querySelector('[data-pg-add-to-cart]');
    const quantity = form.querySelector('[data-pg-quantity]');
    const primaryVariant = form.querySelector('[data-pg-primary-variant]');
    const variantSelect = form.querySelector('[data-pg-variant-select]');
    const twoDiscount = Number(wrapper.dataset.pgTwoDiscount || 0);
    const threeDiscount = Number(wrapper.dataset.pgThreeDiscount || 0);

    const selectedMode = () => Number(modes.find((input) => input.checked)?.dataset.pgBundleMode || 1);

    const syncMode = () => {
      const mode = selectedMode();
      let activePanel = null;
      offers.forEach((offer) => {
        const input = offer.querySelector('[data-pg-bundle-mode]');
        offer.classList.toggle('pg-product__offer--selected', Boolean(input?.checked));
      });
      panels.forEach((panel) => {
        const active = Number(panel.dataset.pgBundlePanel) === mode;
        if (active) activePanel = panel;
        panel.hidden = !active;
        panel.querySelectorAll('[data-pg-bundle-select]').forEach((select) => {
          select.disabled = !active;
        });
        const price = form.querySelector(`[data-pg-bundle-price="${panel.dataset.pgBundlePanel}"]`);
        if (price) {
          const selects = [...panel.querySelectorAll('[data-pg-bundle-select]')];
          price.textContent = formatPrice(selectionTotal(selects, twoDiscount, threeDiscount));
        }
      });
      if (quantity) quantity.value = '1';

      const singleOption = variantSelect?.selectedOptions[0];
      const singleAvailable = variantSelect
        ? singleOption?.dataset.available === 'true'
        : primaryVariant?.dataset.available === 'true';
      const bundleAvailable = activePanel
        ? [...activePanel.querySelectorAll('[data-pg-bundle-select]')].every((select) => {
            const option = select.selectedOptions[0];
            return Boolean(option?.value) && !option.disabled && option.dataset.available === 'true';
          })
        : false;
      const canSubmit = mode === 1 ? singleAvailable : bundleAvailable;
      if (button) {
        button.disabled = !canSubmit;
        button.textContent = canSubmit ? 'Kosárba teszem' : 'Elfogyott';
        button.dataset.pgOriginalText = button.textContent;
      }
    };

    modes.forEach((input) => input.addEventListener('change', syncMode));
    panels.forEach((panel) => {
      panel.querySelectorAll('[data-pg-bundle-select]').forEach((select) => {
        select.addEventListener('change', syncMode);
      });
    });
    syncMode();

    if (variantSelect) {
      variantSelect.addEventListener('change', syncMode);
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const mode = selectedMode();
      let items = [];

      if (mode === 1) {
        const id = Number(variantSelect?.value || primaryVariant?.value);
        if (id) items = [{ id, quantity: 1 }];
      } else {
        const panel = form.querySelector(`[data-pg-bundle-panel="${mode}"]`);
        items = [...(panel?.querySelectorAll('[data-pg-bundle-select]') || [])].map((select) => ({
          id: Number(select.value),
          quantity: 1,
        }));
      }

      if (items.length !== mode || items.some((item) => !item.id)) {
        setButtonState(button, false, 'Válassz színt minden táskához');
        return;
      }

      try {
        setButtonState(button, true, 'Hozzáadás…');
        await addItemsToCart(items);
        setButtonState(button, true, 'A kosárban ✓');
        goToCart();
      } catch (error) {
        setButtonState(button, false, error.message);
        window.setTimeout(() => setButtonState(button, false), 3000);
      }
    });
  };

  const initVideos = (scope = document) => {
    const videos = [...scope.querySelectorAll('.pg-home video, .pg-product video')];
    const desktopHeroVideo = scope.querySelector('[data-pg-desktop-video]');

    if (desktopHeroVideo && desktopHeroVideo.dataset.pgResponsiveBound !== 'true') {
      desktopHeroVideo.dataset.pgResponsiveBound = 'true';
      const desktopMedia = window.matchMedia('(min-width: 750px)');
      const syncDesktopHero = () => {
        desktopHeroVideo.autoplay = desktopMedia.matches;
        if (!desktopMedia.matches) {
          desktopHeroVideo.pause();
          return;
        }

        if (desktopHeroVideo.dataset.pgSourceLoaded !== 'true') {
          desktopHeroVideo.querySelectorAll('source[data-src]').forEach((source) => {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
          });
          desktopHeroVideo.dataset.pgSourceLoaded = 'true';
          desktopHeroVideo.load();
        }
        desktopHeroVideo.play().catch(() => {});
      };

      syncDesktopHero();
      desktopMedia.addEventListener?.('change', syncDesktopHero);
    }

    if (!('IntersectionObserver' in window)) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      videos.forEach((video) => {
        video.autoplay = false;
        video.pause();
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && video.autoplay) {
            video.play().catch(() => {});
          } else if (!entry.isIntersecting) {
            video.pause();
          }
        });
      },
      { rootMargin: '160px 0px', threshold: 0.2 }
    );
    videos.forEach((video) => {
      if (video.dataset.pgObserved === 'true') return;
      video.dataset.pgObserved = 'true';
      observer.observe(video);
    });
  };

  const initMobileMenus = (scope = document) => {
    scope.querySelectorAll('.pg-site-header__mobile-menu').forEach((menu) => {
      if (menu.dataset.pgBound === 'true') return;
      menu.dataset.pgBound = 'true';
      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => menu.removeAttribute('open'));
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.removeAttribute('open');
          menu.querySelector('summary')?.focus();
        }
      });
    });
  };

  const initPeakGear = (scope = document) => {
    scope.querySelectorAll('[data-pg-bundle-form]').forEach((form) => {
      const bundle = form.closest('[data-pg-bundle]');
      if (bundle) initHomeBundle(bundle);
    });
    scope.querySelectorAll('[data-pg-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-pg-product-form]').forEach(initProductForm);
    initMobileMenus(scope);
    initVideos(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPeakGear());
  } else {
    initPeakGear();
  }

  document.addEventListener('shopify:section:load', (event) => initPeakGear(event.target));
})();
